import { useState, useRef, useCallback, useEffect } from 'react';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';

import { useThemeContext } from '../../../../contexts/ThemeContext';
import { resolveColor } from '../../theme/utils/resolveColor';
import { isMobile } from '../../../../utils/utils';
import { getOrCreateBrowserKey } from '../../../../utils/fingerprint_utils';
import { ErrorPage } from '../../../ErrorPage';
import { PrivatePayrollAuctionLoading } from '../Loading';
import { PoweredByQITech } from '../components/PoweredByQITech';
import * as S from './styles';

interface FaceRecognitionProps {
    signData: any;
    setSignerStep: (step: string) => void;
    setSignData: (data: any) => void;
    setAlreadySigned: (v: boolean) => void;
    setCanceled: (v: boolean) => void;
    setExpired: (v: boolean) => void;
    signerHash?: string;
    appTheme?: string;
}

const SIGNED_STATUSES = ['signed', 'sending_sign_receipt', 'receipt_sent', 'analyzing', 'analyzed', 'completed'];

// ── Desktop: QR code polling view ────────────────────────────────────────────

interface DesktopProps {
    signData: any;
    setSignerStep: (step: string) => void;
    setAlreadySigned: (v: boolean) => void;
    setCanceled: (v: boolean) => void;
    setExpired: (v: boolean) => void;
    primary: string;
    clientThemeSettings: any;
}

const DesktopView = ({
    signData,
    setSignerStep,
    setAlreadySigned,
    setCanceled,
    setExpired,
    primary,
    clientThemeSettings,
}: DesktopProps) => {
    const [qrCodeUrl, setQrCodeUrl] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const config = { headers: { Authorization: signData.signer_token } };

        const handleResponse = (data: any) => {
            if (data.signature_url) setQrCodeUrl(data.signature_url);
            if (SIGNED_STATUSES.includes(data.status)) {
                if (data.status !== 'signed') setAlreadySigned(true);
                setSignerStep('signed');
            } else if (data.status === 'canceled') {
                setCanceled(true);
            } else if (data.status === 'expired') {
                setExpired(true);
            }
        };

        if (signData.signer_data?.callback_url) {
            const interval = setInterval(() => {
                axios.get('/signer', config).then((r) => handleResponse(r.data)).catch(() => {});
            }, 4000);
            setLoading(false);
            const timeout = setTimeout(() => clearInterval(interval), 1_200_000);
            return () => { clearInterval(interval); clearTimeout(timeout); };
        }

        axios.get('/signer', config)
            .then((r) => { handleResponse(r.data); })
            .catch((err) => { if (err.response?.status === 401) setSignerStep('login'); })
            .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (loading || !qrCodeUrl) return <PrivatePayrollAuctionLoading />;

    return (
        <S.PageWrapper>
            <S.LogoSection>
                {clientThemeSettings?.logo_url && (
                    <S.LogoImage src={clientThemeSettings.logo_url} alt="logo" />
                )}
            </S.LogoSection>

            <S.ScrollContent>
                <S.Title $color={primary}>Prova de vida</S.Title>
                <S.Subtitle>
                    Escaneie o QR Code abaixo com a câmera do seu celular para continuar a verificação.
                </S.Subtitle>
                <S.QRWrapper>
                    <QRCodeSVG value={qrCodeUrl} width={200} height={200} />
                </S.QRWrapper>
                <S.Subtitle>
                    Mantenha esta tela aberta enquanto realiza a prova de vida no celular.
                </S.Subtitle>
            </S.ScrollContent>

            <S.Footer>
                <PoweredByQITech />
            </S.Footer>
        </S.PageWrapper>
    );
};

// ── Mobile: auto-launch SDK fullscreen ───────────────────────────────────────

export const PrivatePayrollAuctionFaceRecognition = ({
    signData,
    setSignerStep,
    setSignData,
    setAlreadySigned,
    setCanceled,
    setExpired,
}: FaceRecognitionProps) => {
    const { clientThemeSettings } = useThemeContext();
    const primary = resolveColor('primary', clientThemeSettings);

    const [error, setError] = useState({ has_error: false, status: 0 });
    const [isSdkReady, setIsSdkReady] = useState(false);
    const [isTokenNeeded, setIsTokenNeeded] = useState(false);
    const [sdkOpen, setSdkOpen] = useState(false);

    const sdkContainerRef = useRef<HTMLDivElement>(null);
    const instanceRef = useRef<any>(null);

    // ── Session key ──────────────────────────────────
    const getClientSessionKey = useCallback(async () => {
        if (!signData.signer_token) {
            setError({ has_error: true, status: 401 });
            return null;
        }
        try {
            const config = { headers: { Authorization: signData.signer_token } };
            const response = await axios.get('signer/liveness_client_session_key', config);
            const clientSessionKey = response.data.client_session_key;
            if (!clientSessionKey) throw new Error('No client_session_key found.');
            return clientSessionKey as string;
        } catch (err: any) {
            const status = (err.response || {}).status;
            setError({ has_error: true, status: [401, 403, 409].includes(status) ? status : 500 });
            return null;
        }
    }, [signData.signer_token]);

    // ── Success ──────────────────────────────────────
    const handleSuccess = useCallback(async (result: any) => {
        const imageKey = result.data;
        let browserKey: string | null = null;
        try { browserKey = await getOrCreateBrowserKey(); } catch (_) {}

        const payload = {
            event_date: new Date().toISOString(),
            image_key: imageKey,
            step: 'face_recognition',
            device_data: { device_key: browserKey },
        };
        const config = { headers: { Authorization: signData.signer_token } };
        try {
            const response = await axios.patch('/signer', payload, config);
            setSignData({ ...signData, ...response.data });
            setSignerStep(response.data.next_step);
        } catch (err: any) {
            const status = (err.response || {}).status;
            setError({ has_error: true, status: [401, 403, 409].includes(status) ? status : 500 });
        }
    }, [signData, setSignData, setSignerStep]);

    // ── Error ────────────────────────────────────────
    const handleError = useCallback((err: any) => {
        setSdkOpen(false);
        if (err?.reason === 'INVALID_TOKEN') {
            setIsTokenNeeded(true);
        } else if (err?.reason === 'USER_CANCELED') {
            setSignerStep('document_display');
        } else {
            setError({ has_error: true, status: 500 });
        }
    }, [setSignerStep]);

    // ── SDK init ─────────────────────────────────────
    useEffect(() => {
        const hostComponent = sdkContainerRef.current;
        if (!hostComponent) return;

        const webFaceRecon = new (window as any).ZaigWebFaceRecon.WebFaceRecon(hostComponent)
            .setLogLevel('debug')
            .setSessionId(signData.session_id)
            .setThemeConfiguration({
                buttonColor: clientThemeSettings?.button_color,
                fontColor: clientThemeSettings?.font_color,
                backgroundColor: clientThemeSettings?.background_color,
            })
            .setShowInvalidTokenScreen(true)
            .setShowBackButton(signData.signature_settings?.document_display_before_face_recognition !== false);

        if (process.env.REACT_APP_ENV !== 'production') {
            webFaceRecon.setSandboxEnvironment();
        }

        instanceRef.current = webFaceRecon.build();
        instanceRef.current.initialize()
            .then(() => setIsSdkReady(true))
            .catch(() => setError({ has_error: true, status: 500 }));

        return () => { hostComponent.innerHTML = ''; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ── Auto-launch SDK as soon as it is ready ───────
    useEffect(() => {
        if (isSdkReady) setIsTokenNeeded(true);
    }, [isSdkReady]);

    // ── Open SDK when token is needed ────────────────
    useEffect(() => {
        if (!isSdkReady || !instanceRef.current || !isTokenNeeded) return;
        const run = async () => {
            const token = await getClientSessionKey();
            if (token) {
                setIsTokenNeeded(false);
                setSdkOpen(true);
                instanceRef.current.open(token)
                    .then((result: any) => { setSdkOpen(false); handleSuccess(result); })
                    .catch(handleError);
            }
        };
        run();
    }, [isSdkReady, isTokenNeeded, getClientSessionKey, handleSuccess, handleError]);

    // ── Desktop: skip SDK entirely ───────────────────
    if (!isMobile()) {
        return (
            <DesktopView
                signData={signData}
                setSignerStep={setSignerStep}
                setAlreadySigned={setAlreadySigned}
                setCanceled={setCanceled}
                setExpired={setExpired}
                primary={primary}
                clientThemeSettings={clientThemeSettings}
            />
        );
    }

    if (error.has_error) {
        return (
            <ErrorPage
                error={error}
                redirectToLogin={() => setSignerStep('login')}
                resetError={() => setError({ has_error: false, status: 0 })}
                clientThemeSettings={clientThemeSettings}
            />
        );
    }

    return (
        <>
            {/* Loading shown until SDK opens */}
            {!sdkOpen && <PrivatePayrollAuctionLoading />}

            {/* SDK container — always in DOM so the ref is valid on init; fullscreen when open */}
            <div style={{
                position: 'fixed',
                inset: 0,
                backgroundColor: primary,
                zIndex: sdkOpen ? 1000 : -1,
                opacity: sdkOpen ? 1 : 0,
                pointerEvents: sdkOpen ? 'auto' : 'none',
            }}>
                <div ref={sdkContainerRef} style={{ width: '100%', height: '100%' }} />
            </div>
        </>
    );
};
